// client/src/components/BidEstimation/VendorQuotes/QuoteReviewTable.js
import React, { useState, useEffect } from 'react'
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Chip, Typography, Box, TextField, Select, MenuItem, IconButton,
  CircularProgress, Snackbar
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import SaveIcon from '@material-ui/icons/Save'
import ImportExportIcon from '@material-ui/icons/ImportExport'
import Alert from '@material-ui/lab/Alert'
import axios from 'axios'
import CatalogMatchDropdown from './CatalogMatchDropdown'

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(2) },
  headerBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  headerInfo: { display: 'flex', gap: theme.spacing(2), alignItems: 'center', flexWrap: 'wrap' },
  unmapped: {},
  mapped: { backgroundColor: '#e8f5e9' },
  new_created: { backgroundColor: '#e3f2fd' },
  skipped: { backgroundColor: '#f5f5f5', opacity: 0.7 }
}))

const confidenceBadge = {
  high: { label: 'High Confidence', color: '#4caf50' },
  medium: { label: 'Medium Confidence', color: '#ff9800' },
  low: { label: 'Low Confidence', color: '#f44336' }
}

export default function QuoteReviewTable ({ quoteId, onBack }) {
  const classes = useStyles()
  const [quote, setQuote] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [importing, setImporting] = useState(false)
  const [snack, setSnack] = useState(null)

  const fetchQuote = async () => {
    setLoading(true)
    try {
      const { data } = await axios.get(`/api/bid-estimation/vendor-quotes/${quoteId}`)
      setQuote(data)
    } catch (e) {
      console.error('Failed to fetch quote:', e)
    }
    setLoading(false)
  }

  useEffect(() => { fetchQuote() }, [quoteId])

  const updateLineItem = (index, field, value) => {
    const updated = { ...quote }
    updated.lineItems = [...updated.lineItems]
    updated.lineItems[index] = { ...updated.lineItems[index], [field]: value }
    setQuote(updated)
  }

  const handleCatalogMatch = (index, catalogItemId, mappingStatus) => {
    const updated = { ...quote }
    updated.lineItems = [...updated.lineItems]
    updated.lineItems[index] = {
      ...updated.lineItems[index],
      catalogItemId,
      mappingStatus
    }
    setQuote(updated)
  }

  const handleSkip = (index) => {
    updateLineItem(index, 'mappingStatus', 'skipped')
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const { data } = await axios.put(`/api/bid-estimation/vendor-quotes/${quoteId}/line-items`, {
        lineItems: quote.lineItems,
        vendor: quote.vendor
      })
      setQuote(data)
      setSnack({ severity: 'success', message: 'Changes saved' })
    } catch (e) {
      setSnack({ severity: 'error', message: 'Save failed: ' + (e.response?.data?.error || e.message) })
    }
    setSaving(false)
  }

  const handleImport = async () => {
    const mappedCount = quote.lineItems.filter(
      li => li.mappingStatus === 'mapped' || li.mappingStatus === 'new_created'
    ).length

    if (mappedCount === 0) {
      setSnack({ severity: 'warning', message: 'No items mapped. Map items to catalog before importing.' })
      return
    }

    if (!window.confirm(`Import ${mappedCount} items into Material Catalog?`)) return

    // Save first, then import
    setImporting(true)
    try {
      await axios.put(`/api/bid-estimation/vendor-quotes/${quoteId}/line-items`, {
        lineItems: quote.lineItems,
        vendor: quote.vendor
      })
      const { data } = await axios.post(`/api/bid-estimation/vendor-quotes/${quoteId}/import`)
      setSnack({
        severity: 'success',
        message: `Imported ${data.imported} items, updated ${data.catalogUpdated} catalog entries, skipped ${data.skipped}`
      })
      fetchQuote()
    } catch (e) {
      const msg = e.response?.status === 409
        ? 'This quote has already been imported'
        : 'Import failed: ' + (e.response?.data?.error || e.message)
      setSnack({ severity: 'error', message: msg })
    }
    setImporting(false)
  }

  if (loading) return <CircularProgress />
  if (!quote) return <Typography color='error'>Quote not found</Typography>

  const badge = confidenceBadge[quote.parseConfidence] || confidenceBadge.low

  return (
    <div className={classes.root}>
      <Box className={classes.headerBar}>
        <Box display='flex' alignItems='center' style={{ gap: 8 }}>
          <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
          <Typography variant='h6'>
            {quote.vendor || 'Unknown Vendor'} — Quote {quote.quoteNumber || ''}
          </Typography>
          <Chip
            size='small'
            label={badge.label}
            style={{ backgroundColor: badge.color, color: '#fff' }}
          />
          <Chip size='small' label={quote.status} />
        </Box>
        <Box display='flex' style={{ gap: 8 }}>
          <Button
            variant='outlined'
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || quote.status === 'imported'}
          >
            Save
          </Button>
          <Button
            variant='contained'
            color='primary'
            startIcon={<ImportExportIcon />}
            onClick={handleImport}
            disabled={importing || quote.status === 'imported'}
          >
            {quote.status === 'imported' ? 'Imported' : 'Import to Catalog'}
          </Button>
        </Box>
      </Box>

      {/* Header Info */}
      <Box className={classes.headerInfo} mb={2}>
        <TextField
          label='Vendor'
          value={quote.vendor || ''}
          onChange={(e) => setQuote({ ...quote, vendor: e.target.value })}
          size='small'
          variant='outlined'
          disabled={quote.status === 'imported'}
        />
        <Typography variant='body2'>
          Date: {quote.quoteDate ? new Date(quote.quoteDate).toLocaleDateString() : '—'}
        </Typography>
        <Typography variant='body2'>
          Expires: {quote.expirationDate ? new Date(quote.expirationDate).toLocaleDateString() : '—'}
        </Typography>
        <Typography variant='body2'>
          Total: <strong>${(quote.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
        </Typography>
        <Typography variant='body2'>
          {quote.lineItems?.length || 0} line items
        </Typography>
      </Box>

      {/* Line Items Table */}
      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align='right'>Qty</TableCell>
              <TableCell align='right'>Price</TableCell>
              <TableCell>UOM</TableCell>
              <TableCell align='right'>Extension</TableCell>
              <TableCell>Catalog Match</TableCell>
              <TableCell align='center'>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(quote.lineItems || []).map((item, idx) => (
              <TableRow key={item._id || idx} className={classes[item.mappingStatus] || ''}>
                <TableCell>{item.lineNumber}</TableCell>
                <TableCell>
                  <Typography variant='caption'>{item.category || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='caption' style={{ fontFamily: 'monospace' }}>
                    {item.vendorSku || '—'}
                  </Typography>
                </TableCell>
                <TableCell>{item.description}</TableCell>
                <TableCell align='right'>{item.quantity}</TableCell>
                <TableCell align='right'>${item.unitPrice?.toFixed(2)}</TableCell>
                <TableCell>{item.uom}</TableCell>
                <TableCell align='right'>${item.extension?.toFixed(2)}</TableCell>
                <TableCell>
                  {quote.status !== 'imported' ? (
                    <CatalogMatchDropdown
                      value={item.catalogItemId}
                      lineItem={item}
                      onChange={(catId, status) => handleCatalogMatch(idx, catId, status)}
                    />
                  ) : (
                    <Chip size='small' label={item.mappingStatus} />
                  )}
                </TableCell>
                <TableCell align='center'>
                  {quote.status !== 'imported' && item.mappingStatus !== 'skipped' && (
                    <Button size='small' onClick={() => handleSkip(idx)}>Skip</Button>
                  )}
                  {item.mappingStatus === 'skipped' && (
                    <Button size='small' onClick={() => updateLineItem(idx, 'mappingStatus', 'unmapped')}>
                      Unskip
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {quote.parseError && (
        <Box mt={2}>
          <Alert severity='warning'>Parse warning: {quote.parseError}</Alert>
        </Box>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
      >
        {snack && <Alert severity={snack.severity}>{snack.message}</Alert>}
      </Snackbar>
    </div>
  )
}
